import { AutomationRunner } from "encampy";

import { automateEPlanLogin } from "./portalLogin/eplan";

/**
 * Multi-phase automation that extracts invoice number and uses it
 * to navigate to the E-Plan payment.
 */
export function getEPlanNavigateToPaymentAutomation(
  username: string,
  password: string,
  reportingYear: number,
  portalReportId: string
): (runner: AutomationRunner) => Promise<void> {
  return async (runner: AutomationRunner) => {
    // Phase 1: Login and navigate to invoice page
    await runner.run(async (tab) => {
      await automateEPlanLogin(tab, username, password);

      // Select filing year and click continue
      await tab.waitForElement("select[name='filingYear']", { timeout: 5000 });
      await tab.select("select[name='filingYear']", reportingYear.toString());
      await tab.click('input[type="submit"][value="Continue"]');
      await tab.waitForNavigation({ timeout: 10000 });

      // Navigate to invoice page
      await tab.navigate("https://tier2.erplan.net/onlinefiling/invoice.htm");
      await tab.waitForElement("table#InvoiceNotPaid", { timeout: 10000 });
    });

    // Phase 2: Extract the invoice number from the page
    const results = await runner.run(async (tab) => {
      // Get the invoice number from the row containing the portal report ID
      await tab.evaluateXPath(
        // This is very specific to the E-Plan invoice page structure, but it's the most straightforward way to get the invoice number from the portal report ID.
        // Only matches rows where the status is "Not Filed"
        `//table[@id='InvoiceNotPaid']//tr[td[contains(text(),'(FacID:${portalReportId})')] and td[contains(text(),'Not Filed')]]/ancestor::table[1]/ancestor::td[1]/ancestor::tr[1]/preceding-sibling::tr[2]/td[1]/text()`,
        XPathResult.STRING_TYPE
      );
    });

    const invoiceNumber = results
      .find((r): r is typeof r & { xpathResult: string } => typeof r.xpathResult === "string")
      ?.xpathResult.trim();

    if (!invoiceNumber) {
      throw new Error(
        `Could not find the invoice number of an unpaid invoice for portal report: ${portalReportId}`
      );
    }

    // // Phase 3: Navigate directly to payment using extracted invoice number
    await runner.run(async (tab) => {
      const directPaymentUrl = `https://tier2.erplan.net/onlinefiling/payments/paymentprocess.htm?id=${invoiceNumber}&type=all`;
      await tab.navigate(directPaymentUrl);
    });
  };
}
